import { useState, useEffect, useCallback } from "react";
import { Layers, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MODES = [
  { id: "off", label: "Off" },
  { id: "shadow", label: "Shadow (log only)" },
  { id: "live", label: "Live for everyone" },
];

const rate = (row, key) => (row.turns ? `${(((row[key] || 0) / row.turns) * 100).toFixed(1)}%` : "-");

export const ConversationLayerControls = () => {
  const [data, setData] = useState(null);
  const [number, setNumber] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/conversation-layer"); setData(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/admin/conversation-layer", patch);
      setData(data);
      toast.success("Conversation layer updated");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
      return false;
    }
  };

  if (!data) return null;
  return (
    <section data-testid="conv-layer-panel" className="rounded-xl border border-[--w-border] bg-[--w-card] p-5">
      <div className="mb-4 flex items-center gap-2">
        <Layers className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-base font-semibold">Conversation layer rollout</h2>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="conv-layer-mode-group">
        {MODES.map((m) => (
          <Button
            key={m.id}
            size="sm"
            data-testid={`conv-layer-mode-${m.id}`}
            variant={data.mode === m.id ? "default" : "outline"}
            onClick={() => save({ mode: m.id })}
            className={data.mode === m.id ? "bg-blue-600 hover:bg-blue-700" : "border-[--w-border] bg-transparent text-[--w-text-2]"}
          >
            {m.label}
          </Button>
        ))}
      </div>
      <p className="mt-3 text-xs text-[--w-text-3]">Listed numbers always get the new layer live, whatever the mode.</p>
      <div className="mt-3 flex gap-2">
        <Input
          data-testid="conv-layer-number-input"
          placeholder="+91 98xxxxxxxx"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          className="max-w-xs border-[--w-border] bg-[--w-bg]"
        />
        <Button
          size="sm"
          data-testid="conv-layer-add-number"
          onClick={async () => { if (await save({ add_number: number })) setNumber(""); }}
          disabled={!number.trim()}
        >
          Add number
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="conv-layer-numbers">
        {data.numbers.map((n) => (
          <span key={n.hash} className="flex items-center gap-1 rounded-full bg-stone-100 px-3 py-1 font-mono-data text-xs">
            {n.masked}
            <button data-testid={`conv-layer-remove-${n.hash.slice(0, 8)}`} onClick={() => save({ remove_hash: n.hash })}>
              <X className="h-3 w-3 text-[--w-text-2] hover:text-[--w-text]" />
            </button>
          </span>
        ))}
      </div>
      <table className="mt-4 w-full text-left text-xs" data-testid="conv-layer-stats">
        <thead className="text-[--w-text-3]">
          <tr><th>Day</th><th>Mode</th><th>Turns</th><th>p95 end to end</th><th>Avg model ms</th><th>Slow model</th><th>Slow + retry</th><th>Avg research s</th><th>Ack fired</th><th>JSON repair</th><th>Fallback</th><th>Loop guard</th><th>Cap fired</th></tr>
        </thead>
        <tbody className="font-mono-data text-[--w-text-2]">
          {data.stats.map((r) => (
            <tr key={`${r.day}-${r.mode}`}>
              <td>{r.day}</td><td>{r.mode}</td><td>{r.turns || 0}</td>
              <td data-testid={`conv-layer-p95-${r.day}-${r.mode}`}>{r.p95_e2e_ms ? `${(r.p95_e2e_ms / 1000).toFixed(2)} s` : "-"}</td>
              <td>{r.model_turns ? Math.round(r.model_ms_sum / r.model_turns) : "-"}</td>
              <td>{r.model_turns ? `${(((r.slow_model || 0) / r.model_turns) * 100).toFixed(1)}%` : "-"}</td>
              <td>{r.slow_model_retry || 0}</td>
              <td>{r.research_n ? (r.research_ms_sum / r.research_n / 1000).toFixed(1) : "-"}</td>
              <td>{rate(r, "ack_fired")}</td>
              <td>{rate(r, "repair_json")}</td>
              <td>{r.turns ? `${((Object.keys(r).filter((k) => k.startsWith("fallback_")).reduce((s, k) => s + r[k], 0) / r.turns) * 100).toFixed(1)}%` : "-"}</td>
              <td>{(r.guard_loop_options || 0) + (r.guard_identical_reply || 0) + (r.guard_loop_rephrase || 0)}</td>
              <td>{r.guard_discovery_cap || 0}</td>
            </tr>
          ))}
          {!data.stats.length && <tr><td colSpan={13} className="py-2 text-[--w-text-3]">No turns logged yet.</td></tr>}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-[--w-text-2]" data-testid="conv-layer-order-downloads">
        Order downloads (paid): {(data.order_downloads || []).length
          ? data.order_downloads.map((r) => `${r.day}: ${r.n}`).join(" · ")
          : "none yet"}
      </p>
    </section>
  );
};
